import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Modal, Button, Container, Row, Col, Form } from "react-bootstrap";
import AssetServices from "../../../services/AssetServices";
import UserServices from "../../../services/UserServices";
import Swal from "sweetalert2";

const AssestAddModal = React.memo(({ show, handleClose, item }) => {
  console.log("itemass", item)
  const [assetForms, setAssetForms] = useState([]);
  const [assetCount, setAssetCount] = useState(0);
  const [remainingLimit, setRemainingLimit] = useState(0);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // ✅ Fetch assets by inventory
  const fetchAssetsByInventory = useCallback(async () => {
    try {
      if (item?._id) {
        setLoading(true);

        const res = await AssetServices.getAssestbyInventoryId(item._id);
        console.log("📦 API Response:", res.data);

        const assets = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.data)
            ? res.data.data
            : [];

        const count = assets.length;
        setAssetCount(count);

        const currentQty = Number(item?.currentStock || 0);
        const remaining = Math.max(currentQty - count, 0);
        setRemainingLimit(remaining);

        console.log("🔍 Inventory Debug Info:");
        console.log("➡️ Inventory ID:", item._id);
        console.log("➡️ Current Stock (from item):", currentQty);
        console.log("➡️ Assets Found (via API):", count);
        console.log("➡️ Remaining to Create:", remaining);

        if (remaining > 0) {
          setAssetForms([
            {
              vendor: "",
              serialNumber: "",
              model: "",
              assignedTo: "",
              purchaseDate: "",
              warrantyExpiry: "",
              condition: "Good",
              status: "Available",
              remarks: "",
              equipmentNo: "",
              purchasedAmount: "",
              invoiceNo: "",
              active: true,
            },
          ]);

        } else {
          setAssetForms([]);
        }
      }
    } catch (err) {
      console.error("❌ Error fetching assets:", err);
      setAssetForms([]);
    } finally {
      setLoading(false);
    }
  }, [item]);



  // 🔁 Fetch when modal opens
  useEffect(() => {
    if (show) fetchAssetsByInventory();
  }, [show, item, fetchAssetsByInventory]);

  // ✅ Fetch users (for AssignedTo)
  useEffect(() => {
    if (show) {
      UserServices.getAllUser()
        .then((res) => setUsers(res?.data?.data || []))
        .catch((err) => console.error("❌ Error fetching users:", err));
    }
  }, [show]);

  // ✅ Handle input changes
  const handleInputChange = useCallback((index, field, value) => {
    setAssetForms((prev) => {
      const updated = [...prev];
      updated[index][field] = value;
      return updated;
    });
  }, []);

  // ➕ Add form
  const handleAddForm = useCallback(() => {
    setAssetForms((prev) => {
      if (prev.length >= remainingLimit) {
        Swal.fire({
          icon: "warning",
          title: "Limit Reached",
          text: `You can add only up to ${remainingLimit} assets.`,
        });
        return prev;
      }
      return [
        ...prev,
        {
          vendor: "",
          serialNumber: "",
          model: "",
          assignedTo: "",
          purchaseDate: "",
          warrantyExpiry: "",
          condition: "Good",
          status: "Available",
          remarks: "",
          equipmentNo: "",
          purchasedAmount: "",
          invoiceNo: "",
          active: true,
        },
      ];

    });
  }, [remainingLimit]);

  // ➖ Remove form
  const handleRemoveForm = useCallback((index) => {
    setAssetForms((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // 💾 Save assets
 // 💾 Save assets (updated — include equipmentNo, purchasedAmount, invoiceNo)
const handleSave = useCallback(async () => {
  try {
    if (!item?._id) {
      Swal.fire("Error", "Missing inventory reference!", "error");
      return;
    }

    if (assetForms.length === 0) {
      Swal.fire("Info", "No asset forms available to submit.", "info");
      return;
    }

    for (let form of assetForms) {
      if (!form.vendor || !form.vendor.trim()) {
        Swal.fire("Missing Vendor", "Vendor is required!", "warning");
        return;
      }
      if (!form.serialNumber || !form.serialNumber.trim()) {
        Swal.fire(
          "Missing Serial Number",
          "Serial Number is required!",
          "warning"
        );
        return;
      }

      // build payload including new fields
      const payload = {
        inventory: item._id,
        vendor: form.vendor.trim(),
        serialNumber: form.serialNumber.trim(),
        model: form.model?.trim() || '',
        assignedTo: form.assignedTo || null,
        purchaseDate: form.purchaseDate || null,
        warrantyExpiry: form.warrantyExpiry || null,
        condition: form.condition,
        status: form.status,
        remarks: (form.remarks || '').trim(),
        active: form.active,
        // --- NEW FIELDS ---
        equipmentNo: form.equipmentNo ? form.equipmentNo.trim() : '',
        // purchasedAmount must be a Number (or null). Convert safely:
        purchasedAmount: form.purchasedAmount !== undefined && form.purchasedAmount !== ''
          ? Number(form.purchasedAmount)
          : null,
        invoiceNo: form.invoiceNo ? form.invoiceNo.trim() : '',
      };

      // debug option: uncomment to confirm payload in console
      // console.log('Creating asset payload:', payload);

      await AssetServices.createAsset(payload);
    }

    Swal.fire("Success", "Assets added successfully!", "success");
    await fetchAssetsByInventory();
  } catch (error) {
    console.error("❌ Error saving assets:", error);
    Swal.fire(
      "Error",
      error?.response?.data?.message ||
        "Something went wrong while saving assets!",
      "error"
    );
  }
}, [assetForms, item, fetchAssetsByInventory]);


  // ✅ Render UI
  const renderForms = useMemo(() => {
    if (loading) {
      return <p className="text-center text-muted mt-3">Loading assets...</p>;
    }

    if (remainingLimit === 0) {
      return (
        <p className="text-muted text-center mt-3">
          {item?.currentStock === 0
            ? "No action required."
            : "✅ All assets are already created for this inventory item."}
        </p>
      );
    }

    return (
      <>
        {assetForms.map((form, index) => (
          <div key={index} className="border rounded p-3 mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="text-primary mb-0">Asset #{index + 1}</h6>
              {assetForms.length > 1 && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleRemoveForm(index)}
                >
                  − Remove
                </Button>
              )}
            </div>

            {/* Static Info */}
            <Row className="mb-2">
              <Col md="4">
                <label>Part Number</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={item?.masterItem?.partNo || ""}
                  disabled
                />
              </Col>
              <Col md="8">
                <label>Description</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={item?.masterItem?.description || ""}
                  disabled
                />
              </Col>
            </Row>

            {/* Editable Fields */}
            <Row>
              <Col md="4">
                <label>Vendor *</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={form.vendor}
                  onChange={(e) =>
                    handleInputChange(index, "vendor", e.target.value)
                  }
                />
              </Col>
              <Col md="4">
                <label>Serial Number *</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={form.serialNumber}
                  onChange={(e) =>
                    handleInputChange(index, "serialNumber", e.target.value)
                  }
                />
              </Col>
              <Col md="4">
                <label>Model</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={form.model}
                  onChange={(e) =>
                    handleInputChange(index, "model", e.target.value)
                  }
                />
              </Col>
            </Row>
            <Row className="mt-2">
              <Col md="4">
                <label>Equipment No</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={form.equipmentNo}
                  onChange={(e) =>
                    handleInputChange(index, "equipmentNo", e.target.value)
                  }
                  placeholder="Enter equipment number"
                />
              </Col>
              <Col md="4">
                <label>Purchased Amount</label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  value={form.purchasedAmount}
                  onChange={(e) =>
                    handleInputChange(index, "purchasedAmount", e.target.value)
                  }
                  placeholder="Enter amount"
                />
              </Col>
              <Col md="4">
                <label>Invoice No</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={form.invoiceNo}
                  onChange={(e) =>
                    handleInputChange(index, "invoiceNo", e.target.value)
                  }
                  placeholder="Enter invoice number"
                />
              </Col>
            </Row>



            <Row className="mt-2">
              <Col md="4">
                <label>Assigned To</label>
                <Form.Select
                  className="form-select form-select-sm"
                  value={form.assignedTo}
                  onChange={(e) =>
                    handleInputChange(index, "assignedTo", e.target.value)
                  }
                >
                  <option value="">-- Select User --</option>
                  {users.map((user) => (
                    <option key={user._id} value={user._id}>
                      {user.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md="4">
                <label>Purchase Date</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={form.purchaseDate}
                  onChange={(e) =>
                    handleInputChange(index, "purchaseDate", e.target.value)
                  }
                />
              </Col>
              <Col md="4">
                <label>Warranty Expiry</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={form.warrantyExpiry}
                  onChange={(e) =>
                    handleInputChange(index, "warrantyExpiry", e.target.value)
                  }
                />
              </Col>
            </Row>

            <Row className="mt-2">
              <Col md="4">
                <label>Condition</label>
                <Form.Select
                  className="form-select form-select-sm"
                  value={form.condition}
                  onChange={(e) =>
                    handleInputChange(index, "condition", e.target.value)
                  }
                >
                  <option>Excellent</option>
                  <option>Good</option>
                  <option>Fair</option>
                  <option>Damaged</option>
                </Form.Select>
              </Col>
              <Col md="4">
                <label>Status</label>
                <Form.Select
                  className="form-select form-select-sm"
                  value={form.status}
                  onChange={(e) =>
                    handleInputChange(index, "status", e.target.value)
                  }
                >
                  <option>Available</option>
                  <option>In Use</option>
                  <option>Repair</option>
                  <option>Scrapped</option>
                </Form.Select>
              </Col>
              <Col md="4">
                <label>Active</label>
                <Form.Check
                  type="switch"
                  id={`active-switch-${index}`}
                  label={form.active ? "Active" : "Inactive"}
                  checked={form.active}
                  onChange={(e) =>
                    handleInputChange(index, "active", e.target.checked)
                  }
                />
              </Col>
            </Row>

            <Row className="mt-2">
              <Col md="12">
                <label>Remarks</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={form.remarks}
                  onChange={(e) =>
                    handleInputChange(index, "remarks", e.target.value)
                  }
                />
              </Col>
            </Row>
          </div>
        ))}

        {remainingLimit > assetForms.length && (
          <div className="text-center mt-3">
            <Button variant="outline-primary" size="sm" onClick={handleAddForm}>
              + Add Another Asset
            </Button>
          </div>
        )}
      </>
    );
  }, [assetForms, handleInputChange, users, remainingLimit, loading, item]);

  return (
    <Modal className="fade" show={show} onHide={handleClose} size="xl">
      <form>
        <Modal.Header>
          <Modal.Title>
            <i className="fas fa-edit me-2"></i>
            Add Asset Details ({remainingLimit} Remaining of{" "}
            {item?.currentStock || 0})
          </Modal.Title>
          <Button variant="" className="btn-close" onClick={handleClose}></Button>
        </Modal.Header>

        <Modal.Body>
          <Container>
            <div className="text-muted mb-3">
              <strong>{assetCount}</strong> assets already created out of{" "}
              <strong>{item?.currentStock || 0}</strong>.
            </div>
            {renderForms}
          </Container>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="danger light" onClick={handleClose}>
            Close
          </Button>
          {remainingLimit > 0 && assetForms.length > 0 && (
            <Button variant="primary" onClick={handleSave}>
              Save Assets
            </Button>
          )}
        </Modal.Footer>
      </form>
    </Modal>
  );
});

export default AssestAddModal;
